import { isNonEmpty } from '@sgme/fp';
import { tradeRequestApi } from 'apiConnector/quote/trade';
import { parseISO } from 'date-fns';
import { isQuoteEligibleForExecution } from 'epic/utils/pricing/quoteValidity';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, merge, NEVER, of, race } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  mergeMap,
  startWith,
  takeUntil,
  timeout,
  timeoutWith,
  withLatestFrom,
} from 'rxjs/operators';
import { dynamicTradeAuthReceivedThunk, dynamicTradeAuthTimeoutThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { commonTradeAction } from 'state/forms/actions/trade';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { isQuoteTypeTradable } from 'state/quotes/utils';
import type { EpicWithThunk } from 'state/state.common';
import { dateNow } from 'utils/core/now';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeAuthorization } from 'utils/streaming/messages';

// TODO catch Error in Epic
export function tradeAuthorizationForQuoteEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, getSgmeConfiguration, streaming }) =>
    action$.pipe(
      ofType('QUOTE_ACKED'),
      filter(({ quoteType, workspace }) => workspace === 'dynamic' && isQuoteTypeTradable(quoteType)),
      mergeMap(({ tabId, quoteId }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        const tradeAuthTimeoutMs = getSgmeConfiguration().tradeAuthTimeoutMs;
        const tradeRequested$ = action$.pipe(
          ofType('QUOTE_TRADE_REQUESTED'),
          filter(action => action.workspace === 'dynamic' && action.tabId === tabId),
        );
        const tradeAuth$ = tradeAuthorization(streaming).pipe(
          filter(tradeAuth => tradeAuth.quoteId === quoteId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEvent<NextTradeAuthorizationResult>(lightEvent)
              .pipe(catchError(_ => EMPTY)),
          ),
          takeUntil(quoteOrTabRemoved$),
          mergeMap(tradeAuth => {
            if (tradeAuth.status === 'Granted') {
              return of(commonQuoteAction.quoteStale('dynamic', tabId, quoteId, false)).pipe(
                delay(tradeAuth.expirationDelayInSeconds * 1000, appScheduler),
                startWith(dynamicTradeAuthReceivedThunk(tabId, quoteId, tradeAuth)),
                takeUntil(merge(tradeRequested$, quoteOrTabRemoved$)),
              );
            } else {
              return of(dynamicTradeAuthReceivedThunk(tabId, quoteId, tradeAuth));
            }
          }),
        );

        const timeout$ = NEVER.pipe(
          timeoutWith(tradeAuthTimeoutMs, of(dynamicTradeAuthTimeoutThunk(tabId, quoteId)), appScheduler),
        );
        return race([tradeAuth$, timeout$]);
      }),
    );
}

export function postReloadTradeAuthorizationEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, frontLogger, getSgmeConfiguration, getNow }) =>
    action$.pipe(
      ofType('PREVIOUS_QUOTE_LOADED'),
      withLatestFrom(state$),
      filter(
        ([{ quoteId, quote, workspace }, state]) =>
          workspace === 'dynamic' && isQuoteEligibleForExecution(state, quoteId, quote),
      ),
      mergeMap(([{ tabId, quoteId }]) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        const tradeAuthTimeoutMs = getSgmeConfiguration().tradeAuthTimeoutMs;

        const tradeAuth$ = tradeRequestApi(oldHttpClient, tradeAuthTimeoutMs)
          .getTradeAuth(quoteId)
          .pipe(
            takeUntil(quoteOrTabRemoved$),
            mergeMap(tradeAuth => {
              if (tradeAuth.status !== 'Granted') {
                return of(dynamicTradeAuthReceivedThunk(tabId, quoteId, tradeAuth));
              }

              let remainingDelay: number = tradeAuth.expirationDelayInSeconds * 1000;
              if (isNonEmpty(tradeAuth.expiresOn)) {
                const tradeAuthExpirationTime = parseISO(tradeAuth.expiresOn).getTime();
                if (parseISO(tradeAuth.expiresOn).getTime() > dateNow()) {
                  remainingDelay = tradeAuthExpirationTime - dateNow();
                } else {
                  remainingDelay = dateNow() - tradeAuthExpirationTime;
                }
              }

              return of(commonQuoteAction.quoteStale('dynamic', tabId, quoteId, false)).pipe(
                delay(remainingDelay, appScheduler),
                startWith(dynamicTradeAuthReceivedThunk(tabId, quoteId, tradeAuth)),
                takeUntil(quoteOrTabRemoved$),
              );
            }),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside dynamicTradeAuthorizationEpic requesting Trade Auth: {@FrontError}',
                error,
              );
              return of(dynamicTradeAuthTimeoutThunk(tabId, quoteId));
            }),
          );

        const tradeExec$ = tradeRequestApi(oldHttpClient, tradeAuthTimeoutMs)
          .getTradeExec(quoteId)
          .pipe(
            takeUntil(quoteOrTabRemoved$),
            mergeMap(tradeExec => {
              const now = getNow();
              if (tradeExec.status !== 'FULLY_EXECUTED') {
                return of(commonTradeAction.quoteTradeRejected('dynamic', tabId, quoteId, tradeExec.errors ?? [], now));
              }
              return of(commonQuoteAction.quoteStale('dynamic', tabId, quoteId, false)).pipe(
                takeUntil(quoteOrTabRemoved$),
              );
            }),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside dynamicTradeAuthorizationEpic requesting Trade Exec: {@FrontError}',
                error,
              );
              return EMPTY;
            }),
          );

        const timeout$ = NEVER.pipe(
          timeout({
            each: tradeAuthTimeoutMs,
            with: () => of(dynamicTradeAuthTimeoutThunk(tabId, quoteId)),
            scheduler: appScheduler,
          }),
        );
        return race([tradeAuth$, tradeExec$, timeout$]);
      }),
    );
}
